/*
 * /patient/health-records — read-only health summary for the logged-in patient
 */
import { cn, Container, Page, PageHeader, Placeholder } from "@emr/react-ui"
import { Button } from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import {
	Activity,
	CalendarDays,
	Check,
	FileText,
	FlaskConical,
	Pencil,
	Pill,
	Stethoscope,
	X,
} from "lucide-react"
import { useState } from "react"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"
import { PatientPortalFooter } from "../../components/patient-portal-footer"

export const Route = createFileRoute("/patient/health-records")({
	component: PatientHealthRecordsPage,
	loader: rolePageLoader(["patient"]),
})

function PatientHealthRecordsPage() {
	const { data: patient } = trpc.patient.getByFilter.useQuery({ where: { active: true } })

	const { data: rx, isLoading: rxLoading } =
		trpc.prescription.getMyLatestPrescription.useQuery()

	const patientId = patient?.id ?? ""

	const { data: diagnoses, isLoading: dxLoading } = trpc.patientDiagnosis.getAllByFilter.useQuery(
		{ where: { patientId, active: true } },
		{ enabled: !!patientId },
	)

	const { data: treatmentPlans, isLoading: tpLoading } = trpc.treatmentPlan.getAllByFilter.useQuery(
		{ where: { patientId, active: true } },
		{ enabled: !!patientId },
	)

	const { data: enrollments = [], isLoading: enrollLoading, refetch: refetchEnrollments } =
		trpc.patientLpEnrollment.getMyEnrollments.useQuery()

	return (
		<Page>
			<Container>
				<PageHeader title="Health Records" />

				<div className="grid gap-6 md:grid-cols-2">
					{/* ─── Current Prescription ─────────────────────────────── */}
					<Section
						icon={<Pill className="w-4 h-4" />}
						title="Current Prescription"
						loading={rxLoading}
					>
						{!rx ? (
							<Placeholder title="No active prescription" />
						) : (
							<div className="space-y-3 text-sm">
								{rx.amount != null && (
									<Row label="Daily Amount" value={`${rx.amount} g / day`} />
								)}
								{(rx.minThc != null || rx.maxThc != null) && (
									<Row
										label="THC"
										value={formatRange(rx.minThc, rx.maxThc, "%")}
									/>
								)}
								{(rx.minCbd != null || rx.maxCbd != null) && (
									<Row
										label="CBD"
										value={formatRange(rx.minCbd, rx.maxCbd, "%")}
									/>
								)}
								{rx.startDate && (
									<Row label="Start" value={fmtDate(rx.startDate)} />
								)}
								{rx.endDate && (
									<Row
										label="Expires"
										value={fmtDate(rx.endDate)}
										highlight={isExpiringSoon(rx.endDate)}
									/>
								)}
								{rx.fullDiagnosisText && (
									<div className="pt-1 text-muted-foreground border-t">
										<p className="text-xs font-medium uppercase tracking-wide mb-1">Diagnosis</p>
										<p className="whitespace-pre-wrap text-xs">{rx.fullDiagnosisText}</p>
									</div>
								)}
								{(rx as any).lpPrescriptions?.length > 0 && (
									<div className="pt-1 border-t space-y-2">
										<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
											Licensed Producers
										</p>
										{(rx as any).lpPrescriptions.map((lp: any, i: number) => (
											<div key={lp.id ?? i} className="rounded-md bg-muted/40 px-3 py-2">
												<p className="font-medium">{lp.lpName ?? "LP"}</p>
												<div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
													{lp.splitAmount != null && (
														<span className="text-xs text-muted-foreground">{lp.splitAmount} g/day</span>
													)}
													{(lp.minThc != null || lp.maxThc != null) && (
														<span className="text-xs text-muted-foreground">
															THC {formatRange(lp.minThc, lp.maxThc, "%")}
														</span>
													)}
													{(lp.minCbd != null || lp.maxCbd != null) && (
														<span className="text-xs text-muted-foreground">
															CBD {formatRange(lp.minCbd, lp.maxCbd, "%")}
														</span>
													)}
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						)}
					</Section>

					{/* ─── Diagnoses ────────────────────────────────────────── */}
					<Section
						icon={<Stethoscope className="w-4 h-4" />}
						title="Diagnoses"
						loading={dxLoading}
					>
						{!diagnoses?.length ? (
							<Placeholder title="No diagnoses on record" />
						) : (
							<ul className="space-y-2 text-sm">
								{diagnoses.map((dx) => (
									<li key={dx.id} className="flex items-start gap-2">
										<Activity className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
										<div>
											<p className="font-medium">
												{(dx as any).diagnosisCode ?? (dx as any).code ?? ""}
												{((dx as any).diagnosisName ?? (dx as any).name) && (
													<span className="font-normal text-muted-foreground ml-1.5">
														— {(dx as any).diagnosisName ?? (dx as any).name}
													</span>
												)}
											</p>
											{(dx as any).type && (
												<p className="text-xs text-muted-foreground capitalize">{(dx as any).type}</p>
											)}
										</div>
									</li>
								))}
							</ul>
						)}
					</Section>

					{/* ─── LP Registrations ─────────────────────────────────── */}
					<Section
						icon={<FileText className="w-4 h-4" />}
						title="LP Registrations"
						loading={enrollLoading}
						className="md:col-span-2"
					>
						{!(enrollments as any[]).length ? (
							<Placeholder title="No LP registrations on file" />
						) : (
							<div className="grid gap-3 sm:grid-cols-2">
								{(enrollments as any[]).map((e) => (
									<LpEnrollmentCard
										key={e.id}
										enrollment={e}
										onUpdated={refetchEnrollments}
									/>
								))}
							</div>
						)}
					</Section>

					{/* ─── Treatment Plans ──────────────────────────────────── */}
					<Section
						icon={<FileText className="w-4 h-4" />}
						title="Treatment Plans"
						loading={tpLoading}
						className="md:col-span-2"
					>
						{!treatmentPlans?.length ? (
							<Placeholder title="No treatment plans on record" />
						) : (
							<div className="grid gap-3 sm:grid-cols-2">
								{treatmentPlans.map((tp) => (
									<div key={tp.id} className="rounded-lg border bg-muted/40 px-4 py-3 text-sm space-y-1">
										{(tp as any).title && (
											<p className="font-medium">{(tp as any).title}</p>
										)}
										{(tp as any).description && (
											<p className="text-muted-foreground text-xs whitespace-pre-wrap line-clamp-4">
												{(tp as any).description}
											</p>
										)}
										{tp.createdAt && (
											<p className="text-xs text-muted-foreground flex items-center gap-1 pt-1">
												<CalendarDays className="w-3 h-3" />
												{fmtDate(tp.createdAt)}
											</p>
										)}
									</div>
								))}
							</div>
						)}
					</Section>

					{/* ─── Allergies / vitals from patient record ───────────── */}
					{patient && (patient.allergies || (patient as any).bloodType) && (
						<Section icon={<FlaskConical className="w-4 h-4" />} title="Medical Info" className="md:col-span-2">
							<div className="flex flex-wrap gap-6 text-sm">
								{patient.allergies && (
									<Row
										label="Allergies"
										value={patient.allergies}
										highlight={patient.allergies.toLowerCase() !== "none"}
									/>
								)}
								{(patient as any).bloodType && (
									<Row label="Blood Type" value={(patient as any).bloodType} />
								)}
							</div>
						</Section>
					)}
				</div>
				<PatientPortalFooter />
			</Container>
		</Page>
	)
}

/* ─── LP Enrollment card (patient editable) ─────────────────────────────── */

function LpEnrollmentCard({ enrollment, onUpdated }: { enrollment: any; onUpdated: () => void }) {
	const [editing, setEditing] = useState(false)
	const [value, setValue] = useState("")

	const hasPending = Boolean(enrollment.pendingLpClientId)

	const update = trpc.patientLpEnrollment.updateMyClientId.useMutation({
		onSuccess: () => {
			setEditing(false)
			onUpdated()
		},
	})

	function startEdit() {
		// Pre-fill with pending value if one exists, otherwise current confirmed value
		setValue(enrollment.pendingLpClientId ?? enrollment.lpClientId ?? "")
		setEditing(true)
	}

	function handleSave() {
		update.mutate({ enrollmentId: enrollment.id, lpClientId: value.trim() || null })
	}

	return (
		<div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm space-y-2">
			<p className="font-medium">{enrollment.producerName ?? "LP"}</p>

			{/* Confirmed ID */}
			<div className="flex items-center justify-between gap-2">
				<div className="min-w-0">
					<span className="text-xs text-muted-foreground">Current ID: </span>
					{enrollment.lpClientId
						? <span className="font-mono">{enrollment.lpClientId}</span>
						: <span className="text-muted-foreground italic text-xs">not set</span>}
				</div>
				{!editing && (
					<Button size="sm" variant="ghost" className="px-2 h-7 shrink-0" onClick={startEdit}>
						<Pencil className="w-3 h-3" />
					</Button>
				)}
			</div>

			{/* Pending badge */}
			{hasPending && !editing && (
				<div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-2.5 py-1.5 flex items-center justify-between gap-2">
					<div>
						<p className="text-xs font-medium text-amber-700 dark:text-amber-400">Awaiting approval</p>
						<p className="text-sm font-mono">{enrollment.pendingLpClientId}</p>
					</div>
					<Button size="sm" variant="ghost" className="px-2 h-7 shrink-0 text-xs" onClick={startEdit}>
						<Pencil className="w-3 h-3" />
					</Button>
				</div>
			)}

			{/* Edit field */}
			{editing && (
				<div className="flex items-center gap-2">
					<input
						autoFocus
						type="text"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						placeholder="Enter your LP Client ID"
						className="flex-1 h-8 rounded-md border bg-background px-2 text-sm"
					/>
					<Button size="sm" variant="ghost" onClick={handleSave} disabled={update.isPending} className="px-2">
						<Check className="w-3.5 h-3.5" />
					</Button>
					<Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="px-2">
						<X className="w-3.5 h-3.5" />
					</Button>
				</div>
			)}
			{editing && (
				<p className="text-xs text-muted-foreground">
					Your update will be reviewed by the clinic before taking effect.
				</p>
			)}
		</div>
	)
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function Section({
	icon,
	title,
	loading,
	children,
	className,
}: {
	icon: React.ReactNode
	title: string
	loading?: boolean
	children: React.ReactNode
	className?: string
}) {
	return (
		<div className={cn("rounded-xl border bg-card p-5 space-y-4", className)}>
			<div className="flex items-center gap-2 text-sm font-semibold">
				<span className="text-primary">{icon}</span>
				{title}
			</div>
			{loading ? (
				<Placeholder title="Loading…" />
			) : (
				children
			)}
		</div>
	)
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
	return (
		<div className="flex items-baseline gap-2">
			<span className="text-xs text-muted-foreground w-24 shrink-0">{label}</span>
			<span className={cn("font-medium", highlight && "text-destructive")}>{value}</span>
		</div>
	)
}

function formatRange(min: number | null | undefined, max: number | null | undefined, unit: string): string {
	if (min != null && max != null) return `${min}–${max}${unit}`
	if (min != null) return `≥${min}${unit}`
	if (max != null) return `≤${max}${unit}`
	return "—"
}

function fmtDate(d: Date | string) {
	return new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })
}

function isExpiringSoon(d: Date | string) {
	const ms = new Date(d).getTime() - Date.now()
	return ms > 0 && ms < 30 * 24 * 60 * 60 * 1000
}
